import {
  CashLine,
  Invoice,
  InvoiceStatus,
  Receipt,
  ReceiptCashInput,
  ReceiptCashRequest,
  ReceiptCheckInput,
  ReceiptCheckRequest,
  ReceiptCheckType,
  ReceiptIncome,
  ReceiptIncomeType,
} from '../interfaces/receipt.model';

// I path delle rotte arrivano con lo slash dentro un segmento: va spezzato,
// o il router lo codifica in %2F e la rotta non matcha.
export function toRouteSegments(path: string[]): string[] {
  return path.flatMap(segment => segment.split('/')).filter(segment => segment.length > 0);
}

// Il backend manda le date gia' formattate dd/MM/yyyy: new Date() non le
// riconosce e la DatePipe lancia. Qui rientrano anche gli ISO.
export function toDate(value?: string | Date | null): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const italiana = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);

  if (italiana) {
    return new Date(Number(italiana[3]), Number(italiana[2]) - 1, Number(italiana[1]));
  }

  const compatta = /^(\d{4})(\d{2})(\d{2})$/.exec(value);

  if (compatta) {
    return new Date(Number(compatta[1]), Number(compatta[2]) - 1, Number(compatta[3]));
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateLabel(value?: string | Date | null): string {
  const date = toDate(value);

  if (!date) {
    return '-';
  }

  const giorno = `${date.getDate()}`.padStart(2, '0');
  const mese = `${date.getMonth() + 1}`.padStart(2, '0');

  return `${giorno}/${mese}/${date.getFullYear()}`;
}

// Senza arrotondamento un residuo che deve valere zero non ci arriva mai.
export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function cashTotal(cash: ReceiptCashInput | null): number {
  if (!cash) {
    return 0;
  }

  const banconote = cash.righe
    .filter(riga => riga.quantita > 0)
    .reduce((sum, riga) => sum + riga.taglio * riga.quantita, 0);

  return roundMoney(banconote + (cash.importoMonete || 0));
}

// Il backend non manda importoAssegnato: e' client-side, va inizializzato o
// i totali vanno in NaN.
export function normalizeInvoice(invoice: Invoice): Invoice {
  return {
    ...invoice,
    totale: invoice.totale ?? 0,
    acconto: invoice.acconto ?? 0,
    importoAssegnato: invoice.importoAssegnato ?? 0,
    descrizione: invoice.descrizione ?? '',
  };
}

export function invoiceResiduo(invoice: Invoice): number {
  if (invoice.status === InvoiceStatus.Closed) {
    return 0;
  }

  return roundMoney(invoice.totale - invoice.acconto);
}

export function invoiceResiduoDaCoprire(invoice: Invoice): number {
  return roundMoney(invoiceResiduo(invoice) - invoice.importoAssegnato);
}

export function invoiceSaldata(invoice: Invoice): boolean {
  return invoiceResiduo(invoice) > 0 && invoiceResiduoDaCoprire(invoice) <= 0;
}

export function toCashRequest(cash: ReceiptCashInput): ReceiptCashRequest {
  return {
    righe: cash.righe
      .filter((riga: CashLine) => riga.quantita > 0)
      .map(riga => ({ taglioMoneta: riga.taglio, quantita: riga.quantita })),
    importoMonete: cash.importoMonete || 0,
  };
}

export function toCheckRequest(check: ReceiptCheckInput): ReceiptCheckRequest {
  if (!check.data) {
    throw new Error('toCheckRequest: data assegno mancante.');
  }

  return {
    ...(check.idAssegno ? { idAssegno: check.idAssegno } : {}),
    abi: check.abi,
    cab: check.cab,
    nomeBanca: check.banca,
    numeroAssegno: check.numeroAssegno,
    dataAssegno: check.data.toISOString(),
    tipoAssegno: check.tipo,
    importo: check.importo,
  };
}

export function formatMoney(value: number): string {
  return `€ ${value.toFixed(2)}`;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
}


export function cashInputFromBreakdown(
  righe: { valoreTaglio: number; quantita: number; importo: number }[],
): ReceiptCashInput {
  return {
    righe: righe
      .filter(riga => riga.valoreTaglio > 0 && riga.quantita > 0)
      .map(riga => ({ taglio: riga.valoreTaglio, quantita: riga.quantita })),
    importoMonete: righe
      .filter(riga => riga.valoreTaglio === 0)
      .reduce((sum, riga) => sum + riga.importo, 0),
  };
}

export function cashBreakdownFromInput(cash: ReceiptCashInput | null): {
  righe: { valoreTaglio: number; quantita: number }[];
  importoMonete: number;
} {
  return {
    righe: (cash?.righe ?? []).map(riga => ({
      valoreTaglio: riga.taglio,
      quantita: riga.quantita,
    })),
    importoMonete: cash?.importoMonete ?? 0,
  };
}

export function checkInputFromItem(item: {
  abi: string;
  cab: string;
  numero: string;
  banca: string;
  importo: number;
  data: Date | string | null;
  tipo: string;
  id?: string | null;
}): ReceiptCheckInput {
  return {
    idAssegno: item.id ?? null,
    abi: item.abi.trim(),
    cab: item.cab.trim(),
    numeroAssegno: item.numero.trim(),
    banca: item.banca.trim(),
    importo: item.importo,
    data: item.data instanceof Date ? item.data : item.data ? new Date(item.data) : null,
    tipo: (item.tipo as ReceiptCheckType) || null,
  };
}

export function checkItemFromInput(check: ReceiptCheckInput): {
  abi: string;
  cab: string;
  numero: string;
  banca: string;
  importo: number;
  data: Date | null;
  tipo: string;
  id?: string | null;
} {
  return {
    id: check.idAssegno ?? null,
    abi: check.abi,
    cab: check.cab,
    numero: check.numeroAssegno,
    banca: check.banca,
    importo: check.importo,
    data: check.data,
    tipo: check.tipo ?? '',
  };
}

export function incomeFromReceipt(receipt?: Receipt | null): Partial<ReceiptIncome> {
  if (!receipt) {
    return {};
  }

  const assegno = receipt.assegni?.[0];

  if (assegno) {
    const check: ReceiptCheckInput = {
      idAssegno: assegno.id,
      abi: assegno.abi,
      cab: assegno.cab,
      numeroAssegno: assegno.numeroAssegno,
      banca: assegno.nomeBanca,
      importo: assegno.importo,
      data: assegno.dataAssegno ? new Date(assegno.dataAssegno) : null,
      tipo: (assegno.tipoAssegno as ReceiptCheckType) ?? null,
    };

    return { incomeType: ReceiptIncomeType.Check, check, cash: null };
  }

  const righe = (receipt.contanti ?? [])
    .filter(riga => !!riga.taglioMoneta && !!riga.quantita)
    .map(riga => ({ taglio: riga.taglioMoneta as number, quantita: riga.quantita as number }));

  const importoMonete = (receipt.contanti ?? []).reduce(
    (sum, riga) => sum + (riga.importoMonete ?? 0),
    0,
  );

  if (righe.length === 0 && importoMonete === 0) {
    return {};
  }

  return {
    incomeType: ReceiptIncomeType.Cash,
    cash: { righe, importoMonete },
    check: null,
  };
}
